import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FlaskConical, Loader2 } from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import { Button, Message } from '../admin/ui';
import * as Api from './PersonAdminApi';
import { errorText } from './personAdminModel';
import { formatMoment } from './personAdminParts';

// AI research of one person (PersonResearchController): start a job, see whether it is running,
// failed or done. A job starts PENDING, becomes RUNNING and ends SUCCEEDED or FAILED; while the latest
// one is PENDING or RUNNING the card asks for the person's jobs every POLL_MS and, once it has
// finished, tells its owner through `onChanged` so the person is read again and the change set the
// research proposed appears without a manual reload. The polling stops when the job has finished and
// when the card goes away. It reads the jobs itself; the person (and so the change sets) belongs to
// the owner. Used by the person detail screen and the program screen's person panel.
export const POLL_MS = 5000;

const ACTIVE = ['PENDING', 'RUNNING'];
const isActive = (job) => !!job && ACTIVE.includes(job.status);

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const Spinner = styled(Loader2)`animation: ${spin} 1s linear infinite;`;

const Status = styled.p`margin: 0 0 .6rem; font-size: .85rem; color: #334155; display: flex; gap: .4rem; align-items: center; flex-wrap: wrap;`;

const ResearchCard = ({ personId, onChanged }) => {
  const intl = useIntl();
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  useEffect(() => {
    let gone = false;
    Api.fetchResearchJobs(personId).then((all) => { if (!gone) setJobs(all || []); }).catch(() => {});
    return () => { gone = true; };
  }, [personId]);

  const latest = jobs[0] || null;
  const running = isActive(latest);

  useEffect(() => {
    if (!running) return undefined;
    let stopped = false;
    const timer = setInterval(async () => {
      try {
        const all = (await Api.fetchResearchJobs(personId)) || [];
        if (stopped) return;
        setJobs(all);
        if (!isActive(all[0])) {
          stopped = true;
          clearInterval(timer);
          onChangedRef.current?.();
        }
      } catch (err) {
        // a failed poll is tried again on the next tick
      }
    }, POLL_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [running, personId]);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await Api.startResearch(personId, 'GENERAL');
      setJobs((await Api.fetchResearchJobs(personId)) || []);
    } catch (err) {
      setError(errorText(err, intl.formatMessage({ id: 'personAdmin.research.failed', defaultMessage: 'Could not start research.' })));
    } finally {
      setBusy(false);
    }
  }, [personId, intl]);

  return (
    <div>
      <h2 style={{ fontSize: '1rem', margin: '0 0 .6rem' }}>
        <FormattedMessage id="personAdmin.research.title" defaultMessage="AI research" />
      </h2>
      <Status role="status">
        {!latest && <FormattedMessage id="personAdmin.research.none" defaultMessage="No research has run for this person yet." />}
        {running && (
          <>
            <Spinner size={15} aria-hidden="true" />
            <FormattedMessage id="personAdmin.research.running" defaultMessage="Research is running. The result appears below by itself." />
          </>
        )}
        {latest?.status === 'SUCCEEDED' && (
          <FormattedMessage id="personAdmin.research.done" defaultMessage="Research finished {date}. What it found is in the proposed changes below."
            values={{ date: formatMoment(intl, latest.completedAt || latest.createdAt) || '' }} />
        )}
        {latest?.status === 'FAILED' && (
          <FormattedMessage id="personAdmin.research.lastFailed" defaultMessage="The last research failed ({date})."
            values={{ date: formatMoment(intl, latest.completedAt || latest.createdAt) || '' }} />
        )}
      </Status>
      {latest?.status === 'FAILED' && latest.errorMessage && (
        <Message error role="alert">
          <FormattedMessage id="personAdmin.research.error" defaultMessage="The last job failed: {message}" values={{ message: latest.errorMessage }} />
        </Message>
      )}
      {error && <Message error role="alert">{error}</Message>}
      <Button type="button" disabled={busy || running} onClick={start}>
        <FlaskConical size={15} />
        {latest
          ? <FormattedMessage id="personAdmin.research.again" defaultMessage="Research again" />
          : <FormattedMessage id="personAdmin.research.start" defaultMessage="Start research" />}
      </Button>
      {jobs.length > 1 && (
        <details style={{ marginTop: '.75rem', fontSize: '.8rem', color: '#475569' }}>
          <summary style={{ cursor: 'pointer' }}>
            <FormattedMessage id="personAdmin.research.jobs" defaultMessage="All research jobs ({count})" values={{ count: jobs.length }} />
          </summary>
          <ul style={{ margin: '.4rem 0 0', paddingLeft: '1.1rem', lineHeight: 1.6 }}>
            {jobs.map((job) => (
              <li key={job.id}>#{job.id} · {job.status} · {formatMoment(intl, job.createdAt) || '—'}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default ResearchCard;
